import parentApi from "store/parentApi"
import { convertQueryObjToString, localisedValue } from "utils"
import { dateTimeFunction } from "utils/date"
import actionItemsListingTransformHelper from "./actionItemsListingTransformHelper"
import meetingDetailTransformHelper from "./meetingDetailTransformer"
import meetingListTransformHelper from "./meetingListTransformHelper"
import taskTypeTransformHelper from "./taskTypeTransformHelper"
import suggestedTimesTransformer from "./suggestedTimesTransformer"
import defaultAgendasTransformer from "./defaultAgendasTransformer"
import attendeeStatsTransformer from "./attendeeStatsTransformer"
import { getEndPointArgs } from "hooks/useQueryUpdate"

const managementApi = parentApi.injectEndpoints({
  endpoints: build => ({
    getMeetingList: build.query({
      query: ({ pageIndex, pageLength, reset, timeStamp, pageOffset, pastMeetings, ...p }) => {
        const params = { ...p }
        // TODO: Need to remove this need implementation from backend
        //
        const checkDate = key => {
          if (!!params[key]) {
            const dateR = params[key].split(",")
            params[key] = encodeURIComponent(
              `{"from":"${dateTimeFunction({
                date: dateR[0],
                timeZone: true,
                apiRequest: true,
              })}","to":"${dateTimeFunction({
                date: dateR[1],
                timeZone: true,
                apiRequest: true,
              })}"}`
            )
          }
        }
        checkDate("scheduledTime")
        return `managementMeetings?&${convertQueryObjToString({
          pageOffset: params?.observation ? Math.max(0, pageOffset) : pastMeetings ? pageOffset - 1 : pageOffset,
          pastMeetings,
          ...params,
        })}`
      },
      transformResponse: (response, meta, args) => meetingListTransformHelper(response, args),
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName
      },
      merge: (currentMeetingsHash, newHash, { arg }) => {
        if (arg.reset) {
          return newHash
        }
        let incomingMeeting = newHash[arg.pageIndex]
        return { ...currentMeetingsHash, ...newHash }
      },

      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg
      },
      providesTags: ["meeting-list"],
    }),

    getMeetingDetail: build.query({
      query: id => `managementMeetings/${id}`,
      transformResponse: response => meetingDetailTransformHelper(response),
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName
      },
      merge: (_, newDetail) => {
        return newDetail
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg
      },
      providesTags: ["meeting-detail"],
    }),

    getMeetingTypes: build.query({
      query: () => `meetingTypes`,
    }),

    getUserListForMeeting: build.query({
      query: ({ id, ...params }) => `users/reportees/${id}?module=management&${convertQueryObjToString(params)}`,
      transformResponse: response =>
        response.map(e => ({
          ...e,
          id: e.userId,
          label: localisedValue({ object: e, key: "name" }),
          value: e.userId + "",
        })),
    }),

    createMeeting: build.mutation({
      query: values => {
        const payload = {
          title: values.meetingTitle,
          organizerId: values.userId,
          typeId: values.meetingType,
          aiCleanUpPerformed: values?.cleanUpSaved,
          defaultAgendas: values?.defaultAgendas,
          scheduledStartTime: dateTimeFunction({ date: values?.startDate, timeZone: true, apiRequest: true }),
          scheduledEndTime: dateTimeFunction({ date: values?.endDate, timeZone: true, apiRequest: true }),
          attendeeIds: values.addUsers,
          ...(values?.addObservers && { observerIds: [values?.addObservers] }),
          notes: values.meetingNotes,
          ...(values.attachment && {
            noteAttachments: values?.attachment.map(e => {
              return { ...e, file: e?.url?.split(",")[1], name: e.name, type: e.type }
            }),
          }),
          agendas: values?.agendas,
        }
        return { url: `managementMeetings`, method: "POST", body: payload }
      },
      invalidatesTags: (result, error) => {
        return result ? ["meeting-list", "calender-events"] : []
      },
    }),

    editMeeting: build.mutation({
      query: values => {
        const typeOfObservers = typeof values?.addObservers
        const payload = {
          aiCleanUpPerformed: values?.cleanUpSaved,
          ...(values?.defaultAgendas && { defaultAgendas: values?.defaultAgendas }),
          ...(values?.actionType && { actionType: values?.actionType }),
          ...(values?.startDate && {
            scheduledStartTime: dateTimeFunction({ date: values?.startDate, timeZone: true, apiRequest: true }),
          }),
          ...(values?.endDate && {
            scheduledEndTime: dateTimeFunction({ date: values?.endDate, timeZone: true, apiRequest: true }),
          }),
          ...(values.addUsers && { attendeeIds: values.addUsers }),
          ...(values?.createEditMeetingForm && typeOfObservers === "string" && { observerIds: [values?.addObservers] }),
          ...(values?.createEditMeetingForm && typeOfObservers === "undefined" && { observerIds: [] }),
          ...(typeof values.meetingNotes === "string" && { notes: values.meetingNotes }),
          ...(values.agendas && { agendas: values.agendas }),
          ...(values.meetingTitle && { title: values.meetingTitle }),
          ...(!!values?.attachment && {
            noteAttachments: values?.attachment.map(e => {
              return { ...e, file: e?.url?.split(",")[1], name: e.name, type: e.type }
            }),
          }),
          ...(values?.markAllPresent && { markAllPresent: values?.markAllPresent }),
        }
        return { url: `managementMeetings/${values?.meetingId}`, method: "PUT", body: payload }
      },
      invalidatesTags: (result, error, arg) => {
        if (arg?.from === "editNotes") return []
        else return ["meeting-detail", "calender-events"]
      },
    }),

    joinLeaveMeeting: build.mutation({
      query: values => {
        const payload = {
          meetingId: values.meetingId,
          actionType: values.actionType,
          ...values,
        }
        return { url: `managementMeetingAttendees/${values.userId}`, method: "PUT", body: payload }
      },
      invalidatesTags: ["meeting-detail"],
    }),

    deleteMeeting: build.mutation({
      query: id => ({ url: `managementMeetings/${id}`, method: "DELETE" }),
    }),

    addAgendas: build.mutation({
      query: values => {
        const payload = {
          title: values.title,
          url: values.url,
          entityType: "meeting",
          meetingId: values.meetingId,
        }
        return { url: `agendas`, method: "POST", body: payload }
      },
      invalidatesTags: ["meeting-detail"],
    }),

    editAgendas: build.mutation({
      query: values => {
        const payload = {
          title: values?.title,
          url: values?.url,
        }
        return { url: `agendas/${values?.id}`, method: "PUT", body: payload }
      },
    }),

    deleteAgenda: build.mutation({
      query: ({ id, meetingId }) => ({
        url: `agendas/${id}${meetingId ? `?meetingId=${meetingId}` : ``}`,
        method: "DELETE",
      }),
    }),
    getTodoTypes: build.query({
      query: () => `managementTodoTypes`,
      transformResponse: response => taskTypeTransformHelper(response),
    }),

    getAttendeActionItems: build.query({
      query: ({ timeStamp, userId, ...params }) =>
        `managementMeetingAttendees/${userId}/actionItems?${convertQueryObjToString(params)}`,
      // transformResponse: (response, _, args) => attendeeActionItemsTransformer(response, args),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        let { userId, meetingId } = queryArgs

        return { userId, meetingId }
      },

      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg
      },

      merge: (currentActionItems, incomingActionItems, option) => {
        let { stats } = incomingActionItems
        const hasMore = stats.overdues >= 10
        const lastVisitedPage = option.arg.page
        const mergedData = { ...currentActionItems.mergedData, ...incomingActionItems.mergedData }
        return {
          ...currentActionItems,
          ...(option.arg.actionItemType === "todo" && { dueActionItems: incomingActionItems.dueActionItems }),
          ...(option.arg.actionItemType === "last_meeting" && {
            lastMeetingTasks: incomingActionItems.lastMeetingTasks,
          }),
          mergedData,
          stats,
          hasMore,
          lastVisitedPage,
        }
      },
    }),

    addNewActionItem: build.mutation({
      query: values => {
        const payload = {
          ...values,
        }
        return { url: `managementActionItems`, method: "POST", body: payload }
      },
      invalidatesTags: ["action_items"],
    }),

    deleteActionItem: build.mutation({
      query: values => {
        return {
          url: `/managementActionItemAssignees/${values?.item?.assignedTo?.actionItemAssigneeId}`,
          method: "DELETE",
        }
      },
      invalidatesTags: ["action_items"],
    }),

    getActionItemDetail: build.query({
      query: id => `managementActionItems/${id}`,
    }),

    editTodo: build.mutation({
      query: values => {
        const payload = {
          title: values?.title,
          dueDate: dateTimeFunction({ date: values.dueDate, timeZone: true, apiRequest: true }),
          targetType: values.targetType,
          targetCount: values.targetValue,
          description: values.description,
        }
        return { url: `managementTodos/${values.id}`, method: "PUT", body: payload }
      },
    }),

    addTodo: build.mutation({
      query: values => {
        const payload = {
          ...values,
        }
        return { url: `managementTodos`, method: "POST", body: payload }
      },
    }),

    deleteTodo: build.mutation({
      query: id => {
        return { url: `managementTodos/${id}`, method: "DELETE" }
      },
    }),

    updateTodoProgress: build.mutation({
      query: values => {
        const { actionItem } = values
        return {
          url: `managementActionItemAssignees/${actionItem?.assignedTo?.actionItemAssigneeId}/updateProgress`,
          method: "PUT",
        }
      },
      invalidatesTags: ["action_items"],
    }),

    getActiveMeetings: build.query({
      query: () => `managementMeetings/active?meetingType=morning_meeting`,
      transformResponse: response => response?.data,
    }),

    getListActionItems: build.query({
      query: p => {
        const params = { ...p }
        // TODO: Need to remove this need implementation from backend
        //
        const checkDate = key => {
          if (!!params[key]) {
            const dateR = params[key].split(",")
            params[key] = encodeURIComponent(
              `{"from":"${dateTimeFunction({
                date: dateR[0],
                apiRequest: true,
                timeZone: true,
              })}","to":"${dateTimeFunction({ date: dateR[1], apiRequest: true, timeZone: true })}"}`
            )
          }
        }
        checkDate("dueAt")
        return `/managementActionItems?${convertQueryObjToString(params)}`
      },
      transformResponse: response => {
        const transformedData = actionItemsListingTransformHelper(response)
        return transformedData
      },
      providesTags: ["action_items"],
    }),
    getManagementFilterTags: build.query({
      query: params => `/appliedFilters?${convertQueryObjToString(params)}`,
    }),
    postMeetingRecording: build.mutation({
      query: ({ id, data, fileName }) => {
        let getUploadId = data?.find(item => !!item?.uploadId)
        let etagAndUploadId = data
          ?.filter(item => !item?.uploadId)
          ?.map(data => ({ serialNumber: data?.partId, etag: data?.etag }))
          ?.sort((a, b) => a.serialNumber - b.serialNumber)

        let putBody = {
          multipartUpload: {
            fileName: fileName,
            uploadId: getUploadId?.uploadId,
            uploadedParts: etagAndUploadId,
          },
          entityType: "management_meeting",
          entityId: id,
        }
        return { url: `/attachments/finalizeUpload`, method: "POST", body: putBody }
      },
    }),
    getDefaultAgendas: build.query({
      query: params =>
        `/agendaDefaults?meetingTypeId=${params?.meetingType}&designationId=${params?.designation}&teamTypeId=${params?.teamType}`,
      transformResponse: response => defaultAgendasTransformer(response),
    }),
    getObservationForm: build.query({
      query: id => `/managementMeetingFeedbacks/${id}/feedbackForm`,
    }),
    getObservationRecord: build.query({
      query: id => `/managementMeetingFeedbacks/${id}`,
      providesTags: ["observation-record"],
    }),
    addEditObservation: build.mutation({
      query: ({ values, meetingId, observation }) => {
        const observationExists = observation?.length
        const feedbackFields = Object.entries(values)
          .filter(([key]) => !isNaN(key))
          .map(([key, value]) => ({
            ...(observationExists
              ? { id: observation?.find(item => item?.observationCriteriaId == Number(key))?.id }
              : { observationCriteriaId: Number(key) }),
            rating: value,
          }))
        const payload = {
          feedbackFields,
          meetingId: meetingId,
          feedback: values?.feedback,
        }
        return { url: `/managementMeetingFeedbacks`, method: observationExists ? "PUT" : "POST", body: payload }
      },
      invalidatesTags: ["observation-record"],
    }),
    joinLeaveMeetingAsObserver: build.mutation({
      query: values => {
        const payload = {
          meetingId: values.meetingId,
          actionType: values.actionType,
          ...values,
        }
        return { url: `managementMeetingObservers/${values.userId}`, method: "PUT", body: payload }
      },
      invalidatesTags: ["meeting-detail"],
    }),
    getAttendeesStats: build.query({
      query: params => {
        const queryParams = {
          performanceFeedbackMetric: params?.tab,
          reportType: params?.reportType,
          fromDate: params?.fromDate,
          toDate: params?.toDate,
        }
        return `/managementMeetings/${params?.meetingId}/attendeesStats?${convertQueryObjToString(queryParams)}`
      },
      transformResponse: response => {
        return attendeeStatsTransformer(response)
      },
      providesTags: ["attendee_stats"],
    }),
    addPerformanceFeedback: build.mutation({
      query: params => {
        let payload = {
          performanceFeedbackMetric: params?.tab,
          feedbacks: params.feedbacks,
        }
        return {
          url: `managementMeetings/${params?.meetingId}/performanceFeedback`,
          method: "PUT",
          body: payload,
        }
      },
      async onQueryStarted(params, { getState, dispatch, queryFulfilled }) {
        const state = getState()
        const { parentApi, app } = state
        const listingQueryArgs = getEndPointArgs("getAttendeesStats", parentApi)
        const feedback = params?.feedbacks?.[0]
        const listingPatchResult =
          listingQueryArgs &&
          dispatch(
            managementApi.util.updateQueryData("getAttendeesStats", listingQueryArgs, updateRating => {
              updateRating.data.attendeesStats?.forEach(e => {
                if (feedback?.userId == e?.data?.userId) {
                  e.rating = feedback?.rating
                }
              })
            })
          )
        try {
          await queryFulfilled
        } catch {
          listingPatchResult?.undo()
        }
      },
    }),
    getSuggestedMeetingTimes: build.query({
      query: params => `/managementMeetings/suggestedMeetingTime?${convertQueryObjToString(params)}`,
      transformResponse: response => suggestedTimesTransformer(response),
    }),
    aiCleanUpNotes: build.mutation({
      query: notes => {
        const payload = { notes: "<p>" + notes + "</p>" }
        return { url: `/managementMeetings/cleanUpNotes`, method: "POST", body: payload }
      },
    }),
  }),
  overrideExisting: false,
})
export const {
  useGetMeetingListQuery,
  useLazyGetMeetingListQuery,
  useLazyGetMeetingTypesQuery,
  useLazyGetUserListForMeetingQuery,
  useGetUserListForMeetingQuery,
  useCreateMeetingMutation,
  useEditMeetingMutation,
  useDeleteMeetingMutation,
  useGetMeetingDetailQuery,
  useLazyGetMeetingDetailQuery,
  useAddAgendasMutation,
  useDeleteAgendaMutation,
  useJoinLeaveMeetingMutation,
  useMarkAttendanceMutation,
  useLazyGetTodoTypesQuery,
  useAddNewActionItemMutation,
  useLazyGetActionItemDetailQuery,
  useEditTodoMutation,
  useAddTodoMutation,
  useGetAttendeActionItemsQuery,
  useLazyGetAttendeActionItemsQuery,
  useDeleteTodoMutation,
  useUpdateTodoProgressMutation,
  useDeleteActionItemMutation,
  useGetActiveMeetingsQuery,
  useLazyGetActiveMeetingsQuery,
  useGetListActionItemsQuery,
  useLazyGetListActionItemsQuery,
  useLazyGetManagementFilterTagsQuery,
  usePostMeetingRecordingMutation,
  useLazyGetDefaultAgendasQuery,
  useGetObservationFormQuery,
  useGetObservationRecordQuery,
  useAddEditObservationMutation,
  useJoinLeaveMeetingAsObserverMutation,
  useGetAttendeesStatsQuery,
  useLazyGetAttendeesStatsQuery,
  useAddPerformanceFeedbackMutation,
  useLazyGetSuggestedMeetingTimesQuery,
  useAiCleanUpNotesMutation,
  useEditAgendasMutation,
} = managementApi
